'use client';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { ComponentGroupItem, SparePart } from '../../api/spareparts-api';
import { useState, useEffect, useCallback } from 'react';
import { getComponentGroupTemplateSpareParts } from '../../api/spareparts-api';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ShoppingCart } from 'lucide-react';
import { SparePartDialog } from './spare-part-dialog';
import { HotspotImage } from './hotspot-image';

interface ModelComponentGroupSectionProps {
  selectedComponentGroup: ComponentGroupItem;
  setSelectedComponentGroup: (componentGroup: ComponentGroupItem) => void;
  componentGroups: ComponentGroupItem[];
  onBack: () => void;
  className?: string;
}

export function ModelComponentGroupSection({
  selectedComponentGroup,
  setSelectedComponentGroup,
  componentGroups,
  onBack,
  className,
}: ModelComponentGroupSectionProps) {
  const [spareParts, setSpareParts] = useState<SparePart[]>([]);
  const [isLoadingSpareParts, setIsLoadingSpareParts] = useState(false);
  const [sparePartsError, setSparePartsError] = useState<string | null>(null);
  const [hoveredSparePartId, setHoveredSparePartId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<SparePart | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const apiKeys = useGlobalStore(state => state.apiKeys);
  const TEST_API_KEY = (apiKeys as any)?.spareparts || '';
  const [refetchKey, setRefetchKey] = useState(0);

  // Function to refetch the selected component group
  const handleRetryFetchComponentGroup = useCallback(() => {
    if (!selectedComponentGroup?.componentGroupTemplateId) return;
    // Find the latest version of the component group from the list
    const updated = componentGroups.find(
      cg => cg.componentGroupTemplateId === selectedComponentGroup.componentGroupTemplateId,
    );
    if (updated) {
      setSelectedComponentGroup({ ...updated });
    }
    setRefetchKey(prev => prev + 1);
  }, [selectedComponentGroup, componentGroups, setSelectedComponentGroup]);

  useEffect(() => {
    if (!selectedComponentGroup || !componentGroups) return;

    // Only try to find a better component group if the current one has no images
    // and we haven't already tried to find one (to prevent loops)
    if (
      !selectedComponentGroup.assetPathHighResolution &&
      !selectedComponentGroup.assetPathMediumResolution &&
      !selectedComponentGroup.assetPathLowResolution
    ) {
      const componentGroup = componentGroups.find(
        cg => cg.componentGroupTemplateId === selectedComponentGroup.componentGroupTemplateId,
      );
      if (
        componentGroup &&
        (componentGroup.assetPathHighResolution ||
          componentGroup.assetPathMediumResolution ||
          componentGroup.assetPathLowResolution)
      ) {
        setSelectedComponentGroup(componentGroup);
      }
    }
  }, [selectedComponentGroup, componentGroups, setSelectedComponentGroup]);

  // Fetch spare parts when component group is selected
  useEffect(() => {
    let cancelled = false;
    const fetchSpareParts = async () => {
      setSpareParts([]);
      setSparePartsError(null);
      setHoveredSparePartId(null);
      setSelectedItem(null);
      setIsDialogOpen(false);
      if (!selectedComponentGroup.componentGroupTemplateId) {
        setIsLoadingSpareParts(false);
        return;
      }

      setIsLoadingSpareParts(true);
      try {
        const result = await getComponentGroupTemplateSpareParts(
          selectedComponentGroup.componentGroupTemplateId,
          {
            body: { cultureCode: 'en' },
            apiKey: TEST_API_KEY,
          },
        );

        if (cancelled) return;
        if (!result.success || !result.data) {
          throw new Error(result.message || 'Diely sa nepodarilo načítať. Skúste to znova.');
        }

        if (result.success && result.data) {
          const sortedSpareParts = [...(result.data.spareParts || [])].sort(
            (a, b) => a.lineNumber - b.lineNumber,
          );
          setSpareParts(sortedSpareParts);
        } else {
          setSpareParts([]);
        }
      } catch (error) {
        if (cancelled) return;
        setSpareParts([]);
        setSparePartsError(error instanceof Error ? error.message : 'Diely sa nepodarilo načítať.');
      } finally {
        if (!cancelled) setIsLoadingSpareParts(false);
      }
    };

    fetchSpareParts();
    return () => {
      cancelled = true;
    };
  }, [selectedComponentGroup.componentGroupTemplateId, TEST_API_KEY, refetchKey]);

  // Get all hotspots from all spare parts
  const allHotspots = spareParts.flatMap(sp => sp.hotspots || []);

  // Handle hotspot hover to highlight corresponding spare part
  const handleHotspotHover = (hotspotId: string | null) => {
    if (hotspotId) {
      // Find the spare part that contains this hotspot and highlight it
      const sparePartWithHotspot = spareParts.find(sp =>
        sp.hotspots?.some(h => h.id === hotspotId),
      );
      if (sparePartWithHotspot) {
        setHoveredSparePartId(sparePartWithHotspot.id);
      }
    } else {
      setHoveredSparePartId(null);
    }
  };

  return (
    <div className={cn('mt-6 space-y-4', className)}>
      <Button type="button" variant="outline" onClick={onBack}>
        Späť na skupiny dielov
      </Button>
      {/* Component Group Layout */}
      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Left Box - Spare Parts */}
        <div className="h-full w-full lg:w-80 lg:flex-grow">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-4">
              <h4 className="text-lg font-semibold text-gray-900">
                {selectedComponentGroup.componentGroupDescription}
              </h4>
              <p className="text-sm text-gray-600">Spare Parts ({spareParts.length})</p>
            </div>

            {/* Spare Parts List */}
            {isLoadingSpareParts ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="h-6 w-6" />
              </div>
            ) : sparePartsError ? (
              <div role="alert" className="space-y-3 py-4 text-sm text-red-700">
                <p>{sparePartsError}</p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setRefetchKey(value => value + 1)}
                >
                  Skúsiť znova
                </Button>
              </div>
            ) : spareParts.length > 0 ? (
              <div className="max-h-[50vh] gap-0 overflow-y-auto lg:max-h-[70vh]">
                {spareParts.map(sparePart => (
                  <div
                    key={sparePart.id}
                    className={`flex items-start gap-3 rounded border px-3 py-2 hover:bg-gray-50 ${
                      hoveredSparePartId === sparePart.id
                        ? 'border-blue-200 bg-blue-50'
                        : 'border-gray-100'
                    }`}
                    onMouseEnter={() => setHoveredSparePartId(sparePart.id)}
                    onMouseLeave={() => setHoveredSparePartId(null)}
                  >
                    {/* Line Number */}
                    <div className="w-5 flex-shrink-0 text-sm font-medium text-gray-600">
                      {sparePart.lineNumber}
                    </div>

                    {/* Article Number */}
                    <div className="w-28 flex-shrink-0 text-sm text-gray-600">
                      {sparePart.article.articleNumber}
                    </div>

                    {/* Name and Quantity */}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-gray-900">
                        {sparePart.article.name || sparePart.article.articleNumber}
                      </div>
                      <div className="text-xs text-gray-500">Qty: {sparePart.quantity}</div>
                    </div>

                    {/* New Badge */}
                    {sparePart.isNew && (
                      <div className="flex-shrink-0">
                        <span className="inline-block rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                          New
                        </span>
                      </div>
                    )}

                    {/* Cart Button */}
                    <div className="flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => {
                          setSelectedItem(sparePart);
                          setIsDialogOpen(true);
                        }}
                      >
                        <ShoppingCart className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-gray-500">
                No spare parts found for this component group.
              </div>
            )}
          </div>
        </div>

        {/* Right Image Component */}
        <div className="w-full lg:w-1/2">
          <div className="rounded-lg border border-gray-200 bg-white px-1 pb-1 pt-2 md:p-4">
            <HotspotImage
              key={refetchKey}
              imageSrc={selectedComponentGroup.assetPathHighResolution || ''}
              fallbackSrcs={[
                selectedComponentGroup.assetPathMediumResolution || '',
                selectedComponentGroup.assetPathLowResolution || '',
                '/images/spareparts/placeholder_ktm.png',
              ].filter((src): src is string => Boolean(src))}
              alt={selectedComponentGroup.componentGroupDescription}
              hotspots={allHotspots}
              onHotspotHover={handleHotspotHover}
              hoveredHotspotIds={
                hoveredSparePartId
                  ? spareParts.find(sp => sp.id === hoveredSparePartId)?.hotspots?.map(h => h.id) ||
                    []
                  : []
              }
              onRetryFetchComponentGroup={handleRetryFetchComponentGroup}
            />
          </div>
        </div>
      </div>

      {/* ResponsiveDialog for item details */}
      <SparePartDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        sparePart={selectedItem}
      />
    </div>
  );
}
